// ============================================================
// Klient-sida: anropa /api/extract (Cloudflare Pages Function)
// ============================================================

export interface AiExtractResult {
  date?: string
  title?: string
  amount?: number
  amountExVat?: number
  vatAmount?: number
  vatRate?: number
  currency?: string
  accountCode?: number
  accountLabel?: string
  category_guess?: 'kundfakturor' | 'leverantorsfakturor' | 'utlagg' | null
  raw?: string
  error?: string
  /** Felkod från servern (XAI_HTTP_ERROR, GROK_JSON_PARSE_ERROR, INTERNAL_ERROR, etc.) */
  errorCode?: string
  /** HTTP-status från /api/extract — för felsökning */
  httpStatus?: number
  /** Content-type från servern — för felsökning */
  contentType?: string
  /** Början av server-svaret (max 500 tecken) — för felsökning */
  responseSnippet?: string
}

async function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      // reader.result är data:mime/type;base64,XXXXX — ta bara XXXXX
      const result = reader.result as string
      const idx = result.indexOf(',')
      resolve(idx === -1 ? result : result.slice(idx + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * Hämta Firebase ID-token för autentisering mot /api/extract.
 */
async function getFirebaseToken(): Promise<string> {
  const { getAuth } = await import('firebase/auth')
  const auth = getAuth()
  const user = auth.currentUser
  if (!user) throw new Error('Inte inloggad')
  return user.getIdToken()
}

/**
 * Skicka en fil till /api/extract och få strukturerade fält tillbaka.
 * Kräver att användaren är inloggad — skickar Firebase ID-token som Bearer.
 */
export async function aiExtract(file: Blob, signal?: AbortSignal): Promise<AiExtractResult> {
  const mime = file.type || 'application/pdf'
  const base64 = await fileToBase64(file)

  let token: string
  try {
    token = await getFirebaseToken()
  } catch {
    return { error: 'Inte inloggad — kan inte anropa AI-analys' }
  }

  const res = await fetch('/api/extract', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    body: JSON.stringify({ mime, base64 }),
    signal,
  })

  // Read body som text först så vi inte krashar på HTML/SPA-fallback från CF Pages.
  const ct = res.headers.get('content-type') || ''
  const bodyText = await res.text()
  const snippet = bodyText.replace(/\s+/g, ' ').slice(0, 500)

  if (!ct.includes('json')) {
    // CF Pages serverade SPA-html eller en CF-error-sida.
    // Surfaca ett begripligt meddelande istället för "Unexpected token '<'".
    return {
      error: '[NEW-AIEXTRACT v2] /api/extract returnerade icke-JSON (HTTP ' + res.status + ', ' +
        (ct || 'no content-type') + '). Början av svaret: ' + snippet.slice(0, 180),
      errorCode: 'NON_JSON_RESPONSE',
      httpStatus: res.status,
      contentType: ct,
      responseSnippet: snippet,
    }
  }

  let data: AiExtractResult
  try {
    data = JSON.parse(bodyText) as AiExtractResult
  } catch (e) {
    return {
      error: '[NEW-AIEXTRACT v2] Kunde inte parsa JSON (HTTP ' + res.status + '): ' +
        (e as Error).message + ' — body: ' + snippet.slice(0, 200),
      errorCode: 'CLIENT_JSON_PARSE_FAILED',
      httpStatus: res.status,
      contentType: ct,
      responseSnippet: snippet,
    }
  }
  if (!res.ok) {
    return {
      ...data,
      error: data.error || 'HTTP ' + res.status,
      httpStatus: res.status,
      contentType: ct,
      responseSnippet: snippet,
    }
  }
  return data
}
